import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import {
  selectUserRole,
  selectHasModuleAccess,
} from "../../../store/authSlice";
import {
  analyzeKittyReportApi,
  downloadKittyReportExcel,
  addKittyEntryApi,
  listKittyEntriesApi,
  deleteKittyEntryApi,
} from "../../../services/kittyReport";

const ACCOUNTS = ["Fastlink Communications", "Harveer Enterprises"];

const panel =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
const field =
  "rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";
const WHITE = { backgroundColor: "#fff", color: "#1f2937" };

const inr = (n) =>
  n == null
    ? "—"
    : `₹${Number(n).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
const errMsg = (e) => e?.response?.data?.message || "Something went wrong.";
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtDateInput = (d = new Date()) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(
    x.getDate(),
  ).padStart(2, "0")}`;
};
const monthInput = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

// Day-by-day manual entry ledger — date + amount + account, filterable by
// month/account, with a running total. Separate from the upload/analyze
// tool below, which is stateless and reads a Balance Transfer History file.
const KittyDailyEntry = () => {
  const [date, setDate] = useState(fmtDateInput());
  const [amount, setAmount] = useState("");
  const [account, setAccount] = useState(ACCOUNTS[0]);
  const [saving, setSaving] = useState(false);

  const [month, setMonth] = useState(monthInput());
  const [filterAccount, setFilterAccount] = useState("");
  const [entries, setEntries] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await listKittyEntriesApi({ month, account: filterAccount });
      setEntries(data.entries || []);
      setTotalAmount(data.totalAmount || 0);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not load entries", text: errMsg(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, filterAccount]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!date) {
      Swal.fire({ icon: "warning", title: "Pick a date." });
      return;
    }
    if (!Number.isFinite(amt) || amt <= 0) {
      Swal.fire({ icon: "warning", title: "Enter a valid amount." });
      return;
    }
    try {
      setSaving(true);
      await addKittyEntryApi({ date, amount: amt, account });
      setAmount("");
      await load();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not save entry", text: errMsg(err) });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    const ok = await Swal.fire({
      icon: "warning",
      title: "Delete this entry?",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!ok.isConfirmed) return;
    try {
      await deleteKittyEntryApi(id);
      await load();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not delete", text: errMsg(err) });
    }
  };

  return (
    <section className={`${panel} mb-6`}>
      <div className="border-b border-gray-100 px-5 py-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          Daily Entry
        </h2>
        <p className="mt-0.5 text-xs text-gray-400">
          Log each day's amount by account. Filter and total below.
        </p>
      </div>

      <form
        onSubmit={submit}
        className="flex flex-wrap items-end gap-3 border-b border-gray-100 px-5 py-4"
      >
        <div>
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Date
          </span>
          <input
            type="date"
            className={field}
            style={WHITE}
            value={date}
            max={fmtDateInput()}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div>
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Amount
          </span>
          <input
            type="number"
            step="0.01"
            className={`${field} w-32`}
            style={WHITE}
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <div>
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Account
          </span>
          <select
            className={field}
            style={WHITE}
            value={account}
            onChange={(e) => setAccount(e.target.value)}
          >
            {ACCOUNTS.map((a) => (
              <option key={a} value={a} style={WHITE}>
                {a}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          className="rounded-lg bg-amber-500 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-slate-950 transition hover:bg-amber-400 disabled:opacity-50"
          disabled={saving}
        >
          {saving ? "Saving…" : "Add Entry"}
        </button>
      </form>

      <div className="flex flex-wrap items-end gap-3 border-b border-gray-100 px-5 py-3">
        <div>
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Month
          </span>
          <input
            type="month"
            className={field}
            style={WHITE}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </div>
        <div>
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Account
          </span>
          <select
            className={field}
            style={WHITE}
            value={filterAccount}
            onChange={(e) => setFilterAccount(e.target.value)}
          >
            <option value="" style={WHITE}>
              All accounts
            </option>
            {ACCOUNTS.map((a) => (
              <option key={a} value={a} style={WHITE}>
                {a}
              </option>
            ))}
          </select>
        </div>
        <div className="ml-auto text-sm text-gray-500">
          Total: <span className="text-base font-semibold text-emerald-700">{inr(totalAmount)}</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Account</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                  No entries for this range.
                </td>
              </tr>
            ) : (
              entries.map((e) => (
                <tr key={e._id} className="hover:bg-amber-50/30">
                  <td className="px-4 py-2.5 text-gray-700">{fmtDate(e.date)}</td>
                  <td className="px-4 py-2.5 text-gray-700">{e.account}</td>
                  <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-gray-900">
                    {inr(e.amount)}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      type="button"
                      className="text-xs font-semibold text-rose-600 hover:text-rose-800"
                      onClick={() => remove(e._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {entries.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-gray-300 bg-gray-100 font-bold text-gray-800">
                <td className="px-4 py-3" colSpan={2}>
                  Total
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{inr(totalAmount)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
};

const KittyReport = () => {
  const role = useSelector(selectUserRole);
  const hasAccess = useSelector(selectHasModuleAccess("kittyReport", "read"));
  const allowed = role === "super-admin" || hasAccess;

  const [file, setFile] = useState(null);
  const [rate, setRate] = useState(14);
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState(false);
  const [res, setRes] = useState(null);
  const fileRef = useRef(null);

  if (!allowed) {
    return (
      <>
        <PageTitle activeMenu="Kitty Report" motherMenu="Reports" />
        <div className={`${panel} p-10 text-center`}>
          <h3 className="text-lg font-semibold text-gray-900">
            You don't have access to the Kitty Report.
          </h3>
        </div>
      </>
    );
  }

  const run = async (e) => {
    e.preventDefault();
    if (!file) {
      Swal.fire({ icon: "warning", title: "Choose the Balance Transfer History file." });
      return;
    }
    try {
      setBusy(true);
      setRes(null);
      const { data } = await analyzeKittyReportApi({ file, ratePercent: rate });
      setRes(data);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not process file", text: errMsg(err) });
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    if (!file) {
      Swal.fire({ icon: "warning", title: "Choose the file first." });
      return;
    }
    try {
      setDl(true);
      await downloadKittyReportExcel({ file, ratePercent: rate });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Download failed", text: errMsg(err) });
    } finally {
      setDl(false);
    }
  };

  const s = res?.summary;
  const cards = s
    ? [
        ["Total Kitty Amount", s.kittyAmount, "text-emerald-700", `${s.kittyCount} “Add Balance” rows`],
        [`Commission @ ${res.ratePercent}%`, s.commission, "text-amber-600", "on kitty amount only"],
        ["Total Other Payments", s.otherPayments, "text-cyan-700", `${s.otherCount} +ve rows, non-kitty`],
        ["Minus Payments", s.minusPayments, "text-rose-600", `${s.minusCount} rows, deducted by company`],
      ]
    : [];

  return (
    <>
      <PageTitle activeMenu="Kitty Report" motherMenu="Reports" />

      <KittyDailyEntry />

      <section className={`${panel} mb-6`}>
        <div className="border-b border-gray-100 px-5 py-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Balance Transfer History → net totals
          </h2>
          <p className="mt-0.5 text-xs text-gray-400">
            Kitty = sum of <b>TotalTransactionAmount</b> where <b>Type = “Add
            Balance”</b>. Commission is charged on the kitty amount only. Other
            payments = every other positive row; minus payments = every negative
            row.
          </p>
        </div>
        <form onSubmit={run} className="flex flex-wrap items-end gap-3 px-5 py-4">
          <div>
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              File (.xlsx / .csv)
            </span>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className={field}
              style={WHITE}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
          <div>
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Commission %
            </span>
            <input
              type="number"
              step="0.1"
              className={`${field} w-24`}
              style={WHITE}
              value={rate}
              onChange={(e) => setRate(e.target.value)}
            />
          </div>
          <button
            type="submit"
            className="rounded-lg bg-amber-500 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-slate-950 transition hover:bg-amber-400 disabled:opacity-50"
            disabled={busy}
          >
            {busy ? "Processing…" : "Analyze"}
          </button>
          <button
            type="button"
            onClick={download}
            className="rounded-lg border border-emerald-300 bg-emerald-50 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-50"
            disabled={dl || !file}
          >
            {dl ? "Preparing…" : "Download Excel"}
          </button>
        </form>
      </section>

      {res && (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3 text-xs text-gray-500">
            <span className="rounded-full bg-gray-100 px-2 py-0.5 font-semibold text-gray-700">
              {res.partnerCode || "—"}
            </span>
            <span>{res.rowsParsed} rows</span>
            <span>
              {fmtDate(res.dateFrom)} – {fmtDate(res.dateTo)}
            </span>
            {(res.warnings || []).map((w, i) => (
              <span key={i} className="text-amber-700">{w}</span>
            ))}
          </div>

          <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {cards.map(([label, value, tone, hint]) => (
              <div key={label} className={`${panel} px-4 py-3`}>
                <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                  {label}
                </div>
                <div className={`mt-1 text-xl font-semibold tabular-nums ${tone}`}>
                  {inr(value)}
                </div>
                <div className="mt-0.5 text-[10px] text-gray-400">{hint}</div>
              </div>
            ))}
          </div>

          <section className={`${panel} mb-6`}>
            <div className="border-b border-gray-100 px-5 py-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Net summary
              </h2>
            </div>
            <table className="min-w-full text-sm">
              <tbody className="divide-y divide-gray-100">
                {[
                  ["Total Kitty Amount (Type = Add Balance)", s.kittyAmount, false],
                  [`Commission @ ${res.ratePercent}% (on kitty amount)`, s.commission, false],
                  ["Total Other Payments (non-kitty, positive)", s.otherPayments, false],
                  ["Minus Payments (deducted by company)", s.minusPayments, true],
                  ["Net wallet movement (all rows)", s.netWalletMovement, false],
                ].map(([label, value, neg]) => (
                  <tr key={label}>
                    <td className="px-5 py-2.5 text-gray-700">{label}</td>
                    <td
                      className={`px-5 py-2.5 text-right font-semibold tabular-nums ${
                        neg ? "text-rose-600" : "text-gray-900"
                      }`}
                    >
                      {inr(value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className={panel}>
            <div className="border-b border-gray-100 px-5 py-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                By transaction Type
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-center">Rows</th>
                    <th className="px-4 py-3 text-right">Credits (+)</th>
                    <th className="px-4 py-3 text-right">Debits (−)</th>
                    <th className="px-4 py-3 text-right">Net</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(res.byType || []).map((g) => (
                    <tr key={g.type} className="hover:bg-amber-50/30">
                      <td className="px-4 py-2.5 font-medium text-gray-800">
                        {g.type}
                        {g.type.toLowerCase() === "add balance" && (
                          <span className="ml-2 rounded bg-emerald-100 px-1.5 text-[10px] font-bold uppercase text-emerald-700">
                            kitty
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-center tabular-nums">
                        {g.count}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-emerald-700">
                        {inr(g.positive)}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-rose-600">
                        {inr(g.negative)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-semibold tabular-nums">
                        {inr(g.net)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
};

export default KittyReport;
